import { useState } from 'react';
import Sheet from './Sheet';
import Icon from './Icon';
import { PRIMARY } from './CaptureSheets';
import { NumberField } from './SettingsRows';
import { DURATIONS, PLAN_NOTE, giftEnd, vnDate } from '../lib/coach';

// The coach's sheets on the Students tab, numbered as in the UX spec.

const OUTLINE = 'btn h-12 gap-2 rounded-[16px] border-line bg-base-100 text-[15px] font-bold';
const TOGGLE = 'toggle border-base-300 bg-base-300 text-white checked:border-primary checked:bg-primary checked:text-white';

// Seats in use out of the total, as a bar of blocks.
export function SeatBar({ plan }) {
  if (!plan) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: Math.min(plan.seats, 30) }, (_, i) => (
          <span key={i} className={`h-2.5 flex-1 rounded-full ${i < plan.seats_used ? 'bg-primary' : 'bg-base-300'}`} />
        ))}
      </div>
      <p className="text-xs text-muted">Đã dùng {plan.seats_used}/{plan.seats} chỗ · còn {plan.seats_free} chỗ trống</p>
    </div>
  );
}

export function DurationPicker({ value, onChange, plan, from = new Date() }) {
  return (
    <div role="radiogroup" aria-label="Thời gian tặng Pro" className="grid grid-cols-3 gap-2">
      {DURATIONS.map((d) => {
        const end = giftEnd(from, d.id, plan?.period_ends_at);
        return (
          <button key={d.id} type="button" role="radio" aria-checked={value === d.id} onClick={() => onChange(d.id)}
            className={`flex min-h-16 flex-col items-center justify-center rounded-[14px] border-2 px-1 py-2 text-center ${value === d.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
            <span className="text-sm font-bold">{d.label}</span>
            {end && <span className="text-[11px] text-muted">đến {vnDate(end)}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const emptyInvite = () => ({ nickname: '', duration: '1_month', preset: false, calorie_goal: '', protein_goal: '', note: '' });

// ④ "Mời học viên": a nickname only the coach sees, how long the gift lasts, optional targets, then a channel.
// The form's state lives in the Students page, so it survives a trip to "Hết chỗ" (⑤).
export function InviteSheet({ open, form, setForm, plan, busy, error, onClose, onSend }) {
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const ready = form.nickname.trim().length > 0;
  return (
    <Sheet open={open} onClose={onClose} title="Mời học viên" badge={<span className="text-xs text-muted">Dùng 1 chỗ · tặng Pro</span>}>
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[15px] font-semibold">Tên gợi nhớ</span>
          <input className="input h-11 w-full rounded-[14px] border-line text-base" maxLength={40} placeholder="Ví dụ: Hà My"
            value={form.nickname} onChange={(e) => set('nickname')(e.target.value)} />
          <span className="text-xs text-muted">Nếu tài khoản học viên chưa có tên, đây sẽ là tên của họ (họ đổi được trong Hồ sơ).</span>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-[15px] font-semibold">Tặng Pro trong</span>
          <DurationPicker value={form.duration} onChange={set('duration')} plan={plan} />
          <span className="text-xs text-muted">{PLAN_NOTE}</span>
        </div>
        <div className="rounded-[16px] border border-line p-3">
          <label className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-[15px] font-semibold">Đặt sẵn mục tiêu</span>
              <span className="block text-xs text-muted">Học viên vẫn đổi được sau</span>
            </span>
            <input type="checkbox" className={TOGGLE} checked={form.preset} onChange={(e) => set('preset')(e.target.checked)} />
          </label>
          {form.preset && (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex items-center justify-between"><span className="text-sm">Calo mỗi ngày</span>
                <span className="flex items-center gap-2"><NumberField id="inv-cal" unit="kcal" value={form.calorie_goal} onChange={set('calorie_goal')} /></span></div>
              <div className="flex items-center justify-between"><span className="text-sm">Đạm mỗi ngày</span>
                <span className="flex items-center gap-2"><NumberField id="inv-pro" unit="g" accent="text-protein" value={form.protein_goal} onChange={set('protein_goal')} /></span></div>
            </div>
          )}
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-[15px] font-semibold">Lời nhắn <span className="font-normal text-muted">(không bắt buộc)</span></span>
          <input className="input h-11 w-full rounded-[14px] border-line text-base" maxLength={200} placeholder="Cùng tập với mình trên Macro Mate nhé!"
            value={form.note} onChange={(e) => set('note')(e.target.value)} />
        </label>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="button" className={PRIMARY} disabled={!ready || !!busy} onClick={() => onSend('share')}>
          {busy === 'share' ? <span className="loading loading-spinner loading-sm" /> : <Icon name="upload" className="size-5" />} Gửi qua Zalo, Messenger…
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" className={OUTLINE} disabled={!ready || !!busy} onClick={() => onSend('qr')}>
            {busy === 'qr' ? <span className="loading loading-spinner loading-sm" /> : <Icon name="qr" className="size-5" />} Mã QR
          </button>
          <button type="button" className={OUTLINE} disabled={!ready || !!busy} onClick={() => onSend('copy')}>
            {busy === 'copy' ? <span className="loading loading-spinner loading-sm" /> : <Icon name="copy" className="size-5" />} Sao chép link
          </button>
        </div>
        <p className="text-center text-xs text-muted">Lời mời dùng được 1 lần, hết hạn sau 7 ngày (mã QR: 15 phút).</p>
      </div>
    </Sheet>
  );
}

// ⑤ "Hết chỗ": buy one more seat, take back a pending invite, or end coaching a student; then back to ④.
export function NoSeatsSheet({ open, plan, invites, students, onClose, onBuy, onRevoke, onEnd }) {
  return (
    <Sheet open={open} onClose={onClose} title={`Bạn đã dùng hết ${plan?.seats || 0} chỗ`}>
      <div className="flex flex-col gap-4">
        <SeatBar plan={plan} />
        <button type="button" className={PRIMARY} onClick={() => onBuy(1)}>Thêm 1 chỗ</button>
        <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={() => onBuy(null)}>Chọn số chỗ khác</button>
        {(invites.length > 0 || students.length > 0) && <p className="text-sm font-semibold text-muted">Hoặc giải phóng một chỗ</p>}
        <ul className="flex flex-col divide-y divide-line rounded-[16px] border border-line px-3">
          {invites.map((i) => (
            <li key={`i${i.invite_id}`} className="flex min-h-14 items-center justify-between gap-2">
              <span className="min-w-0"><span className="block truncate font-medium">{i.name}</span><span className="block text-xs text-muted">Chờ nhận lời mời</span></span>
              <button type="button" className="btn btn-sm h-9 rounded-full" onClick={() => onRevoke(i)}>Thu hồi</button>
            </li>
          ))}
          {students.map((s) => (
            <li key={`s${s.client_id}`} className="flex min-h-14 items-center justify-between gap-2">
              <span className="min-w-0"><span className="block truncate font-medium">{s.name}</span><span className="block text-xs text-muted">{s.ends_at ? `Pro đến ${vnDate(s.ends_at)}` : 'Đang được tặng Pro'}</span></span>
              <button type="button" className="btn btn-sm h-9 rounded-full" onClick={() => onEnd(s)}>Kết thúc</button>
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  );
}

// ⑩ "Tặng Pro" to a connected student, and ⑭ "Gia hạn Pro" (adds to the current end; no extra seat while it lasts).
export function GiftSheet({ student, mode, plan, busy, error, onClose, onConfirm }) {
  const [duration, setDuration] = useState('1_month');
  const extending = mode === 'extend' && student?.status !== 'gift_ended';
  const from = extending && student?.ends_at ? new Date(student.ends_at) : new Date();
  const end = giftEnd(from, duration, plan?.period_ends_at);
  const label = DURATIONS.find((d) => d.id === duration)?.label;
  return (
    <Sheet open={!!student} onClose={onClose} title={extending ? `Gia hạn Pro cho ${student?.name}` : `Tặng Pro cho ${student?.name}`}>
      {student && (
        <div className="flex flex-col gap-4">
          {extending && student.ends_at && <p className="text-sm">Hiện tại: Pro đến <b>{vnDate(student.ends_at)}</b></p>}
          <DurationPicker value={duration} onChange={setDuration} plan={plan} from={from} />
          {extending
            ? <p className="text-sm">{student.ends_at ? `${vnDate(student.ends_at)} → ` : ''}<b>{end ? vnDate(end) : 'đến khi bạn huỷ'}</b> · không tốn thêm chỗ</p>
            : <SeatBar plan={plan && { ...plan, seats_used: plan.seats_used + 1, seats_free: Math.max(plan.seats_free - 1, 0) }} />}
          <p className="text-xs text-muted">{PLAN_NOTE}</p>
          {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => onConfirm(duration)}>
            {busy && <span className="loading loading-spinner loading-sm" />}
            {extending ? `Gia hạn thêm ${label.toLowerCase()}` : duration === 'until_cancelled' ? 'Tặng Pro đến khi huỷ' : `Tặng ${label} Pro`}
          </button>
        </div>
      )}
    </Sheet>
  );
}

// ⑬ The options for one student.
export function StudentSheet({ student, onClose, onAction }) {
  const gifted = student && ['coaching', 'ending_soon'].includes(student.status);
  const row = 'flex min-h-14 w-full items-center gap-3 text-left text-[15px] font-medium';
  return (
    <Sheet open={!!student} onClose={onClose} title={student?.name}>
      {student && (
        <div className="flex flex-col divide-y divide-line rounded-[16px] border border-line px-4">
          {gifted && <button type="button" className={row} onClick={() => onAction('extend')}><Icon name="clock" className="size-5" /> Gia hạn Pro</button>}
          {!gifted && !student.personal_pro && <button type="button" className={row} onClick={() => onAction('gift')}><Icon name="gift" className="size-5" /> Tặng Pro</button>}
          <button type="button" className={row} onClick={() => onAction('view')}><Icon name="eye" className="size-5" /> Xem nhật ký</button>
          <button type="button" className={row} onClick={() => onAction('targets')}><Icon name="target" className="size-5" /> Đặt mục tiêu</button>
          <button type="button" className={row} onClick={() => onAction('schedule')}><Icon name="calendar" className="size-5" /> Thêm lịch tập</button>
          {gifted && <button type="button" className={row} onClick={() => onAction('stop')}><Icon name="x" className="size-5" /> Ngừng tặng Pro</button>}
          <button type="button" className={`${row} text-error`} onClick={() => onAction('remove')}><Icon name="trash" className="size-5" /> Xoá khỏi danh sách</button>
        </div>
      )}
    </Sheet>
  );
}

// ⑯ Removing a student ends the connection now; a gifted one keeps Pro to its end date unless the coach says otherwise.
export function RemoveStudentSheet({ student, busy, onClose, onConfirm }) {
  const gifted = student && ['coaching', 'ending_soon'].includes(student.status);
  const [keep, setKeep] = useState(true);
  const option = (value, title, text, recommended) => (
    <button type="button" role="radio" aria-checked={keep === value} onClick={() => setKeep(value)}
      className={`flex flex-col gap-0.5 rounded-[16px] border-2 p-3 text-left ${keep === value ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
      <span className="flex items-center gap-2 font-bold">{title}
        {recommended && <span className="rounded-full bg-[#e3f1e9] px-2 py-0.5 text-[10px] font-extrabold text-[#17804f]">NÊN CHỌN</span>}</span>
      <span className="text-sm text-muted">{text}</span>
    </button>
  );
  return (
    <Sheet open={!!student} onClose={onClose} title={`Xoá ${student?.name}?`}>
      {student && (
        <div className="flex flex-col gap-4">
          <p className="text-sm">Bạn sẽ không xem được nhật ký của {student.name} nữa.</p>
          {gifted && (
            <div role="radiogroup" aria-label="Pro của học viên" className="flex flex-col gap-2">
              {option(true, `Giữ Pro đến ${student.ends_at ? vnDate(student.ends_at) : 'hết kỳ gói HLV'}`, 'Chỗ vẫn được giữ đến ngày đó, bạn có thể trả sớm.', true)}
              {option(false, 'Về Free ngay', 'Chỗ được trả lại ngay.')}
              <p className="text-xs text-muted">Không hoàn tiền phần thời gian còn lại.</p>
            </div>
          )}
          <button type="button" className="btn btn-error h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy} onClick={() => onConfirm(gifted ? keep : false)}>
            {busy && <span className="loading loading-spinner loading-sm" />} Xoá khỏi danh sách
          </button>
        </div>
      )}
    </Sheet>
  );
}
